import React, { useCallback, useEffect, useState } from 'react'
import { AdCreative, FBAdPreviewFormat } from '@marketing-milk/interfaces'
import { useGetBusinessID } from '@marketing-milk/frontend'
import {
  invalidateCreativePreview,
  prefetchCreativePreview,
} from 'hooks/creatives/fetch-creative-preview'
import { SurfacePalette } from '../../tokens'
import { CreativePreviewModal } from '../../components/CreativePreviewModal'

interface CreativePreviewActions {
  /** Edit-mode only: open the creative's native editor (new tab). */
  onOpenSource?: (creative: AdCreative) => void
  /** Record an AE-attested client approval (opens the approval modal). */
  onApprove?: (creative: AdCreative) => void
  /** Clear an existing approval (opens the remove-confirm modal). */
  onRemoveApproval?: (creative: AdCreative) => void
}

/**
 * Shared "Preview ad" interaction for the Edit (`CreativeSection`) and Present
 * (`PresentCreativeSection`) creative grids. Owns the preview-modal state and
 * returns the card callback plus the modal to render. Mirrors
 * `useCreativeApproval`: `onModalOpenChange` lets Present lock deck navigation so
 * Arrow/Esc don't move slides or exit the deck while the preview is open.
 *
 * The preview is the only surface that shows the full ad (copy + CTA), so the
 * meeting-time actions live in its header bar. `actions` forwards the host's
 * approve / remove / open-source handlers into the modal. Approve and Remove
 * close the preview first so their (centered) confirm modal doesn't stack on top
 * of this (centered) one.
 *
 * `prefetch` warms a single creative's live preview into the shared cache (used
 * on card hover) so the modal opens instantly. We warm one creative at a time
 * on hover rather than all on mount to avoid ~7 simultaneous live FB fetches.
 */
export function useCreativePreview(
  palette: SurfacePalette,
  onModalOpenChange?: (open: boolean) => void,
  actions: CreativePreviewActions = {}
) {
  const businessID = useGetBusinessID()
  const [previewing, setPreviewing] = useState<AdCreative | undefined>(undefined)
  const open = !!previewing

  useEffect(() => {
    onModalOpenChange?.(open)
    return () => onModalOpenChange?.(false)
  }, [open, onModalOpenChange])

  const prefetch = useCallback(
    (creative: AdCreative) => {
      if (creative.id) {
        prefetchCreativePreview(businessID, creative.id, FBAdPreviewFormat.MOBILE_FEED_STANDARD)
      }
    },
    [businessID]
  )

  const { onApprove, onRemoveApproval, onOpenSource: openSource } = actions
  // Opening the native editor (new tab) means the creative may be edited; drop the
  // cached preview now so the modal's focus-refetch (and any later open) is live,
  // not the pre-edit HTML.
  const onOpenSource = openSource
    ? (creative: AdCreative) => {
        if (creative.id) {
          invalidateCreativePreview(businessID, creative.id, FBAdPreviewFormat.MOBILE_FEED_STANDARD)
        }
        openSource(creative)
      }
    : undefined
  // Approve/Remove open their own centered modal — close the preview first to
  // avoid stacking two centered dialogs. Open-source opens a new tab, so the
  // preview can stay up.
  const closeThen = (handler?: (creative: AdCreative) => void) =>
    handler
      ? (creative: AdCreative) => {
          setPreviewing(undefined)
          handler(creative)
        }
      : undefined

  const modals = previewing && (
    <CreativePreviewModal
      creative={previewing}
      palette={palette}
      onClose={() => setPreviewing(undefined)}
      onApprove={closeThen(onApprove)}
      onRemoveApproval={closeThen(onRemoveApproval)}
      onOpenSource={onOpenSource}
    />
  )

  return { onPreview: setPreviewing, prefetch, modals }
}
